import { CompensationVoucher, ValidationError } from '../../models/home';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { MUTATE_COMPENSATION_CODES } from '../../mutations/common';
import { useApiError } from '../../providers/ApiErrorProvider';
import { useAuthProvider } from '../../providers/AuthProvider';
import { useMutation } from '@apollo/client';
import { VoucherIdObjInterface } from '../../models/app';

interface Props {
    setActiveMenuIndex: Dispatch<SetStateAction<number>>;
}

export const useHome = ({
    setActiveMenuIndex, 
}: Props) => {
    const authContext = useAuthProvider();
    const { setErrorCode } = useApiError();
    const [compensationVouchers, setCompensationVouchers] = useState<CompensationVoucher[]>([]);
    const [isDeviceOffline, setIsDeviceOffline] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [, setVoucherIdQueryTriggered] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<ValidationError>({
        errorCode: 0,
        isError: false
    });

    const [myMutation, { 
        loading: loadingMutation, 
        error: errorMutation, 
        data: dataMutation
    }] = useMutation(MUTATE_COMPENSATION_CODES);

    function skipGetNtpVoucherIds() {
        const ntpVoucherIdObjArray = JSON.parse(
          localStorage.getItem("ntpVoucherIdObjArray") || "[]"
        );

        if(!ntpVoucherIdObjArray?.length) return false;

        // Filter to get only the unused vouchers
        const voucherIdsFreeArray = ntpVoucherIdObjArray.filter(
            (voucher: { used: boolean, ttl: number }) => !voucher?.used && voucher?.ttl > Date.now()
        );
    
        const freeCounts = {
            '15%': 0,
            '20%': 0,
            '25%': 0,
            '30%': 0
        };

        voucherIdsFreeArray.forEach(v => freeCounts[v.voucherSubTypeCode]++);
        const hasLessThan10 = Object.values(freeCounts).some((count) => count < 10);
        return hasLessThan10 ? false : true;
    }

    const fetchVoucherIds = useCallback((overrideSkipCheck: boolean = false) => {
        if (overrideSkipCheck || !skipGetNtpVoucherIds()) {
            const asyncOps = async () => {
                const refreshedToken = await authContext.acquireTokenSilently();
                if (refreshedToken) {
                    setVoucherIdQueryTriggered(true);
                }
            }
            asyncOps();            
        } 
    // eslint-disable-next-line react-hooks/exhaustive-deps       
    }, [authContext]);

    const handleOnClickNewForm = (samePassenger: boolean) => {
        if (samePassenger) {
            sessionStorage.setItem('NewFormSamePassenger','true');
        } else {
            sessionStorage.removeItem('RecentFormValues');
            sessionStorage.removeItem('NewFormSamePassenger');
        }
        setValidationError({
            errorCode: 0,
            isError: false
        });
        setShowConfirmation(false);
        setCompensationVouchers([]);
        document.getElementById('c-page')?.scrollIntoView();
    };

    const syncVoucherIds = useCallback(() => {
        const performMutation = async (payload) => {
            try {
                const { data } = await myMutation({
                    variables: {
                        payload: payload,
                    }
                });
                
                /* 
                    REMOVE the voucher id object if successful mutation call
                    REMOVE from voucherIdsUsedArray also
                    KEEP the voucher id object if NOT successful mutation call
                */

                if (data && data?.alocateVoucher && data?.alocateVoucher.length) {
                    data?.alocateVoucher?.forEach(alocateVoucher => {
                        if (alocateVoucher?.statusCode === 200) {
                            const voucherIdObjArray: VoucherIdObjInterface[] = JSON.parse(localStorage.getItem('ntpVoucherIdObjArray') ?? '[]');
                            const voucherIdObjArrayUpdated: VoucherIdObjInterface[] = voucherIdObjArray.filter(voucher => voucher?.id !== alocateVoucher?.voucherId);
                            localStorage.setItem('ntpVoucherIdObjArray', JSON.stringify(voucherIdObjArrayUpdated));
        
                            const voucherIdsUsedArray: any[] = JSON.parse(localStorage.getItem('voucherIdsUsedArray') ?? '[]');
                            const voucherIdsUsedArrayUpdated: any[] = voucherIdsUsedArray.filter(voucher => voucher?.voucherIds?.every(id => id !== alocateVoucher?.voucherId));
                            localStorage.setItem('voucherIdsUsedArray', JSON.stringify(voucherIdsUsedArrayUpdated));
                        }
                    });
                }                
            } catch (error) {
                console.error('syncVoucherIds error');
                setErrorCode(500);
            }
        };

        /* 
            1. pull the local storage and check if any Voucher IDs has been used - voucherIdsUsedArray,
            if used call the mutation
            Otherwise skip the mutation
        */

        const voucherIdsUsedArray: any[] = JSON.parse(localStorage.getItem('voucherIdsUsedArray') ?? '[]');
        if (voucherIdsUsedArray && voucherIdsUsedArray.length) {
            voucherIdsUsedArray.forEach((voucher, index) => {
                const voucherIdMutationPayload  = {
                    flightLegNumber: voucher?.flightLegNumber,
                    flightMarketingCarrier: voucher?.flightMarketingCarrier,
                    flightScheduledAt: voucher?.flightScheduledAt,
                    passengerEmailAddress: voucher?.passengerEmailAddress,
                    passengerFirstName: voucher?.passengerFirstName,
                    passengerLastName: voucher?.passengerLastName,
                    pnrLocator: voucher?.pnrLocator,
                    voucherIds: voucher?.voucherIds,
                    voucherIssuanceComment: voucher?.voucherIssuanceComment,
                    voucherIssuanceReason: voucher?.voucherIssuanceReason,
                    voucherType: voucher?.voucherType
                }

                if (voucherIdMutationPayload && Object.keys(voucherIdMutationPayload).length) {
                    const asyncOps = async () => {
                        const refreshedToken = await authContext.acquireTokenSilently();
                        if (refreshedToken) {
                            performMutation(voucherIdMutationPayload);
                        }
                    }
                    /*
                        Adding a delay of 5 seconds before calling the mutation for each request
                    */
                    if(index === 0) asyncOps(); 
                    else setTimeout(() => asyncOps(), 5000);               
                }
            });
        }
    }, [authContext, myMutation, setErrorCode]);

    const checkForExpiredVoucherIds = () => {
        const voucherIdObjArray: VoucherIdObjInterface[] = JSON.parse(localStorage.getItem('ntpVoucherIdObjArray') ?? '[]');
        const updatedVoucherIdObjArray = voucherIdObjArray.filter(voucher => voucher.ttl && voucher.ttl > Date.now());
        localStorage.setItem('ntpVoucherIdObjArray', JSON.stringify(updatedVoucherIdObjArray));
        if(!isDeviceOffline) fetchVoucherIds();
    };


    const syncAndPullEvery10Minutes = useCallback(async() => {
        /* 1. avoid calling the sync and pull if device is offline */
        if (isDeviceOffline) return;

        /* 2. get fresh auth token if existing one is expired already */
        const refreshedToken = await authContext.acquireTokenSilently();

        if (refreshedToken) {
            /* 3. Sync Voucher Ids with BE */
            syncVoucherIds();
                
            /* 4. Pull fresh Voucher Ids from BE and store in localStorage after a delay of 1min */
            const delay = 1 * 60 * 1000;
            setTimeout(() => {
                fetchVoucherIds();
            }, delay);
        }  
    }, [authContext, isDeviceOffline, fetchVoucherIds, syncVoucherIds]);
    
    window.addEventListener('offline', () => setIsDeviceOffline(true));

    window.addEventListener('online', () => setIsDeviceOffline(false));

    useEffect(() => {
        setActiveMenuIndex(0);
        fetchVoucherIds();
        syncVoucherIds();
        /* Start the interval when the component mounts, 10 minutes in milliseconds */
        const id = setInterval(syncAndPullEvery10Minutes, 10 * 60 * 1000); 
    
        /* Clear the interval when the component unmounts to prevent memory leaks */
        return () => {
          clearInterval(id);
        };
    }, [fetchVoucherIds, syncVoucherIds, setActiveMenuIndex, syncAndPullEvery10Minutes]);


    useEffect(() => {
        if (errorMutation) {
            console.error('error Mutation');
            setErrorCode(500);
        }
    }, [dataMutation, errorMutation, loadingMutation, setErrorCode]);

    return {
        compensationVouchers,
        isDeviceOffline,
        showConfirmation,
        validationError,
        handleOnClickNewForm,
        setCompensationVouchers,
        setShowConfirmation,
        setValidationError,
        setVoucherIdQueryTriggered
   }
}